// LoginButton.tsx
import { IonButton, IonImg } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { once, run } from '../../helper/electron';
import { useIcons } from '../../hooks/useIcons';
import SpotifyService from '../../services/Spotify.service';
import { useDispatch } from '../../store/hooks';
import {
  AppIdentifiers,
  setToken,
  unsetToken,
} from '../../store/slices/token-state.slice';
import isElectron from '../../utils/isElectron';
import buttonStyle from './login-button.module.scss';

interface LoginButtonProps {
  redirectPath?: string; // Optional prop to define the redirect path
  small?: boolean;
}

const LoginButton: React.FC<LoginButtonProps> = ({
  redirectPath,
  small = false,
}) => {
  const token = useSelector((state) => SpotifyService.getAccessToken());
  const [isLoggedIn, setIsLoggedIn] = useState(!!token);
  const [profile, setProfile] = useState<{ display_name: string } | null>(null);

  const dispatch = useDispatch();
  const { icons } = useIcons();

  useEffect(() => {
    if (!token) {
      setProfile(null);
      setIsLoggedIn(false);
      return;
    }

    setIsLoggedIn(true);
    SpotifyService.getProfile().then((value: any) => {
      setProfile({ display_name: value.display_name });
    });
  }, [token]);

  const handleClick = async () => {
    if (isLoggedIn) {
      dispatch(unsetToken(AppIdentifiers.Spotify));
    } else {
      const currentPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;

      const targetPath = redirectPath ?? currentPath;

      const authUrl = await SpotifyService.getSpotifyAuthorizeUrl(targetPath);

      if (isElectron()) {
        run('loginSpotify', {
          authUrl,
          redirectUri: 'cratehackersx://spotify/callback',
        });
        once(
          'loginSpotify',
          async (result: { code?: string; error?: string }) => {
            if (result.code) {
              try {
                const tokenResponse = await SpotifyService.getToken(
                  result.code,
                );
                if (tokenResponse.access_token) {
                  const expiry = Date.now() + tokenResponse.expires_in * 1000;
                  dispatch(
                    setToken(
                      AppIdentifiers.Spotify,
                      tokenResponse.access_token,
                      tokenResponse.refresh_token,
                      expiry,
                    ),
                  );
                }
              } catch (err) {
                console.error('[LoginButton] Token exchange error:', err);
              }
            }
          },
        );
      } else {
        window.location.href = authUrl;
      }
    }
  };

  return (
    <IonButton
      className={`${buttonStyle.spotifyLoginButton} ${small ? buttonStyle.small : ''}`}
      onClick={handleClick}
      fill="outline"
      expand="block"
    >
      <IonImg src={icons.spotifyIcon} className={buttonStyle.spotifyIcon} />
      {isLoggedIn ? (
        <>
          <div>Log out as {profile?.display_name}</div>
        </>
      ) : (
        <div>Log in with Spotify</div>
      )}
    </IonButton>
  );
};

export default LoginButton;
